import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Resolver } from "react-hook-form";
import {
  insertClientSchema,
  type InsertClient,
  type Client,
} from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

interface ClientFormProps {
  onSubmit: (data: InsertClient) => Promise<void> | void; // Callback na odoslanie údajov
  defaultValues?: Partial<Client>;
  isSubmitting?: boolean;
  onClose?: () => void; // Callback na zatvorenie modálneho okna
}

// Zoznam krajín pre select (oficiálne anglické názvy)
const COUNTRIES = [
  // EÚ krajiny
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  // Mimo EÚ
  'Albania',
  'Argentina',
  'Australia',
  'Belarus',
  'Bosnia and Herzegovina',
  'Brazil',
  'Canada',
  'China',
  'Hong Kong',
  'India',
  'Israel',
  'Japan',
  'Kosovo',
  'Mexico',
  'Moldova',
  'Montenegro',
  'New Zealand',
  'North Macedonia',
  'Norway',
  'Russia',
  'Saudi Arabia',
  'Serbia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Switzerland',
  'Turkey',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
].sort();

// Custom resolver that transforms empty strings to undefined before validation
const clientFormResolver: Resolver<InsertClient> = async (values, context, options) => {
  // Transform empty strings to undefined for optional fields
  // invoiceEmail is not registered in the form, so we set it from email
  const emailValue = values.email && values.email.trim() !== '' ? values.email : undefined;
  const transformedValues = {
    ...values,
    email: emailValue,
    phone: values.phone && values.phone.trim() !== '' ? values.phone : undefined,
    invoiceEmail: emailValue, // invoiceEmail is always the same as email (disabled field)
    businessName: values.businessName && values.businessName.trim() !== '' ? values.businessName : undefined,
    businessId: values.businessId && values.businessId.trim() !== '' ? values.businessId : undefined,
    taxId: values.taxId && values.taxId.trim() !== '' ? values.taxId : undefined,
    vatId: values.vatId && values.vatId.trim() !== '' ? values.vatId : undefined,
    street: values.street && values.street.trim() !== '' ? values.street : undefined,
    city: values.city && values.city.trim() !== '' ? values.city : undefined,
    postalCode: values.postalCode && values.postalCode.trim() !== '' ? values.postalCode : undefined,
    country: values.country && values.country.trim() !== '' ? values.country : undefined,
    company: values.company && values.company.trim() !== '' ? values.company : undefined,
    note: values.note && values.note.trim() !== '' ? values.note : undefined,
    vatRate: values.vatRate && values.vatRate.toString().trim() !== '' ? values.vatRate : undefined,
  };
  
  // Use zodResolver with transformed values
  return zodResolver(insertClientSchema)(transformedValues, context, options);
};

export function ClientForm({
  onSubmit,
  defaultValues,
  isSubmitting,
  onClose,
}: ClientFormProps) {
  console.log('🔧 [ClientForm] defaultValues:', defaultValues);
  console.log('🔧 [ClientForm] street:', defaultValues?.street, 'businessName:', (defaultValues as any)?.businessName);
  console.log('🔧 [ClientForm] ALL defaultValues keys:', defaultValues ? Object.keys(defaultValues) : 'none');
  if (defaultValues) {
    console.log('🔧 [ClientForm] Full defaultValues JSON:', JSON.stringify(defaultValues, null, 2));
    console.log('🔧 [ClientForm] street value:', defaultValues?.street);
    console.log('🔧 [ClientForm] businessName value:', (defaultValues as any)?.businessName);
    console.log('🔧 [ClientForm] postalCode value:', (defaultValues as any)?.postalCode);
  }
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isSubmitSuccessful, errors, isValid },
  } = useForm<InsertClient>({
    resolver: clientFormResolver,
    defaultValues: {
      name: defaultValues?.name || "",
      email: defaultValues?.email || "",
      phone: defaultValues?.phone || "",
      businessId: defaultValues?.businessId || "",
      taxId: defaultValues?.taxId || "",
      vatId: defaultValues?.vatId || "",
      businessName: (defaultValues as any)?.businessName || "",
      street: defaultValues?.street || "",
      city: defaultValues?.city || "",
      postalCode: defaultValues?.postalCode || "",
      country: defaultValues?.country || "Slovakia",
      hourlyRate: defaultValues?.hourlyRate ? Number(defaultValues.hourlyRate) : undefined,
      dueDays: (defaultValues as any)?.dueDays || 14,
      invoiceEmail: (defaultValues as any)?.invoiceEmail || "",
      company: (defaultValues as any)?.company || "",
      note: (defaultValues as any)?.notes || "",
      vatRate: (defaultValues as any)?.vatRate || "",
    },
  });

  // Debug logy pre form state
  console.log('🔧 [ClientForm] Form errors:', errors);
  console.log('🔧 [ClientForm] Form isValid:', isValid);
  console.log('🔧 [ClientForm] Form isSubmitSuccessful:', isSubmitSuccessful);
  
  // Debug konkrétnych errorov
  if (errors.invoiceEmail) {
    console.log('🔧 [ClientForm] invoiceEmail error:', errors.invoiceEmail);
  }

  // Zatvorenie modálneho okna po úspešnom uložení
  useEffect(() => {
    if (isSubmitSuccessful && onClose) {
      onClose();
    }
  }, [isSubmitSuccessful, onClose]);

  const handleFormSubmit = async (data: InsertClient) => {
    console.log('🔧 [ClientForm] handleFormSubmit CALLED with data:', data);
    try {
      console.log('🔧 [ClientForm] Calling onSubmit...');
      
      // Data is already cleaned by the resolver, but ensure invoiceEmail is set
      const cleanedData = {
        ...data,
        invoiceEmail: data.invoiceEmail || data.email || undefined,
      };
      
      // Data is already in camelCase from the form, backend will convert to snake_case
      await onSubmit(cleanedData as InsertClient);
      console.log('🔧 [ClientForm] onSubmit SUCCESS');
      reset(); // Reset formulára po uložení
    } catch (error) {
      // Error sa spracuje v parent komponente
      console.error('🔧 [ClientForm] Form submission error:', error);
      throw error; // Re-throw to prevent react-hook-form from marking as successful
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full sm:grid-cols-4 grid-cols-2 gap-1">
          <TabsTrigger value="basic" className="text-xs sm:text-sm px-1 sm:px-3">Základné údaje</TabsTrigger>
          <TabsTrigger value="business" className="text-xs sm:text-sm px-1 sm:px-3">Firemné údaje</TabsTrigger>
          <TabsTrigger value="address" className="text-xs sm:text-sm px-1 sm:px-3">Adresa</TabsTrigger>
          <TabsTrigger value="invoice" className="text-xs sm:text-sm px-1 sm:px-3">Faktúra</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <div>
            <Label htmlFor="name">Meno / Názov*</Label>
            <Input id="name" {...register("name")} />
            {errors.name && (
              <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" {...register("email")} />
            {errors.email && (
              <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="phone">Telefón</Label>
            <Input id="phone" {...register("phone")} />
            {errors.phone && (
              <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="business" className="space-y-4">
          <div>
            <Label htmlFor="businessName">Názov firmy</Label>
            <Input id="businessName" {...register("businessName")} />
          </div>

          <div>
            <Label htmlFor="businessId">IČO</Label>
            <Input id="businessId" {...register("businessId")} />
          </div>

          <div>
            <Label htmlFor="taxId">DIČ</Label>
            <Input id="taxId" {...register("taxId")} />
          </div>

          <div>
            <Label htmlFor="vatId">IČ DPH</Label>
            <Input 
              id="vatId" 
              {...register("vatId")} 
              placeholder="SK1234567890" 
            />
            {errors.vatId && (
              <p className="text-sm text-destructive mt-1">{errors.vatId.message}</p>
            )}
          </div>

        </TabsContent>

        <TabsContent value="address" className="space-y-4">
          <div>
            <Label htmlFor="street">Ulica</Label>
            <Input id="street" {...register("street")} />
          </div>

          <div>
            <Label htmlFor="city">Mesto</Label>
            <Input id="city" {...register("city")} />
          </div>

          <div>
            <Label htmlFor="postalCode">PSČ</Label>
            <Input id="postalCode" {...register("postalCode")} />
          </div>

          <div>
            <Label htmlFor="country">Krajina</Label>
            <Select
              value={watch("country") || "Slovakia"}
              onValueChange={(value) => setValue("country", value)}
            >
              <SelectTrigger id="country">
                <SelectValue placeholder="Vyberte krajinu" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.country && (
              <p className="text-sm text-destructive mt-1">{errors.country.message}</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="invoice" className="space-y-4">
          <div>
            <Label htmlFor="hourlyRate">Hodinová sadzba (€)</Label>
            <Input
              id="hourlyRate"
              type="number"
              {...register("hourlyRate", { valueAsNumber: true })}
            />
          </div>

          <div>
            <Label htmlFor="dueDays">Splatnosť faktúry (dni)</Label>
            <Input
              id="dueDays"
              type="number"
              {...register("dueDays", { valueAsNumber: true })}
            />
          </div>

          <div>
            <Label htmlFor="note">Poznámka</Label>
            <Textarea id="note" {...register("note")} />
          </div>
        </TabsContent>
      </Tabs>

      <Button 
        type="submit" 
        className="w-full" 
        disabled={isSubmitting}
        onClick={() => console.log('🔧 [ClientForm] Button clicked!', { isSubmitting, errors, isValid })}
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          "Uložiť klienta"
        )}
      </Button>
    </form>
  );
}